<template>
  <div class="container">
    <v-card>
      <v-toolbar class="mt-12" dark color="primary">
        <v-toolbar-title>Gestión de Cajas</v-toolbar-title>
      </v-toolbar>

      <!-- Resumen global de saldos por caja -->
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-chip label color="transparent" text-color="black">
              <h3>Saldos por Caja</h3>
            </v-chip>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="caja in saldosCajas"
            :key="caja.idCaja"
            cols="12"
            sm="6"
            md="4"
          >
            <v-card
              outlined
              class="pa-3"
              :class="{ 'border-primary': cajaSeleccionada === caja.idCaja }"
              @click="seleccionarCaja(caja)"
              style="cursor: pointer"
            >
              <v-row no-gutters>
                <v-col cols="8">
                  <span class="text-subtitle-1 font-weight-bold">{{ caja.nombreCaja }}</span>
                  <v-chip v-if="caja.esDefault" x-small color="primary" class="ml-2">Default</v-chip>
                </v-col>
                <v-col cols="4" class="text-right">
                  <v-icon v-if="cajaSeleccionada === caja.idCaja" color="primary">mdi-check-circle</v-icon>
                </v-col>
              </v-row>
              <v-divider class="my-2"></v-divider>
              <v-row no-gutters class="mb-1">
                <v-col cols="6" class="text-body-2">Ingresos:</v-col>
                <v-col cols="6" class="text-body-2 text-right green--text">{{ caja.totalIngresosString }}</v-col>
              </v-row>
              <v-row no-gutters class="mb-1">
                <v-col cols="6" class="text-body-2">Egresos:</v-col>
                <v-col cols="6" class="text-body-2 text-right red--text">{{ caja.totalEgresosString }}</v-col>
              </v-row>
              <v-divider class="my-1"></v-divider>
              <v-row no-gutters>
                <v-col cols="6" class="text-body-1 font-weight-bold">Saldo:</v-col>
                <v-col cols="6" class="text-body-1 font-weight-bold text-right" :class="caja.saldo >= 0 ? 'green--text' : 'red--text'">{{ caja.saldoString }}</v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- Saldo desde último cierre -->
        <v-row v-if="saldoDesdeCierre && cajaSeleccionada" class="mt-2">
          <v-col cols="12">
            <v-alert type="info" outlined dense>
              <strong>Desde último cierre ({{ saldoDesdeCierre.fechaUltimoCierreString }}):</strong>
              Saldo {{ saldoDesdeCierre.saldoDesdeCierreString }} — {{ saldoDesdeCierre.cantidadMovimientos }} movimientos sin cerrar
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Detalle de movimientos -->
      <v-card-text v-if="cajaSeleccionada">
        <v-row>
          <v-col cols="12">
            <v-chip label color="transparent" text-color="black">
              <h3>Detalle: {{ nombreCajaSeleccionada }}</h3>
            </v-chip>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="4" md="3">
            <v-select
              :items="periodos"
              item-text="texto"
              item-value="valor"
              label="Período"
              v-model="periodoSeleccionado"
              dense
              prepend-icon="mdi-calendar-range"
              @change="cambioPeriodo"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="3" md="2" v-if="periodoSeleccionado === 'personalizado'">
            <v-text-field
              v-model="fechaDesde"
              label="Desde"
              type="date"
              dense
              prepend-icon="mdi-calendar"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="3" md="2" v-if="periodoSeleccionado === 'personalizado'">
            <v-text-field
              v-model="fechaHasta"
              label="Hasta"
              type="date"
              dense
              prepend-icon="mdi-calendar"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="3" md="2">
            <v-btn color="primary" @click="buscarMovimientos" :disabled="!cajaSeleccionada">
              Buscar <v-icon right>mdi-magnify</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <!-- Totales del período -->
        <v-row v-if="resultadoMovimientos" justify="end">
          <v-col cols="12" sm="6" md="4">
            <v-card outlined class="pa-3">
              <v-row no-gutters class="mb-1">
                <v-col cols="7" class="text-body-2">Ingresos (período):</v-col>
                <v-col cols="5" class="text-body-2 font-weight-bold text-right green--text">{{ resultadoMovimientos.totalIngresosString }}</v-col>
              </v-row>
              <v-row no-gutters class="mb-1">
                <v-col cols="7" class="text-body-2">Egresos (período):</v-col>
                <v-col cols="5" class="text-body-2 font-weight-bold text-right red--text">{{ resultadoMovimientos.totalEgresosString }}</v-col>
              </v-row>
              <v-divider class="my-1"></v-divider>
              <v-row no-gutters>
                <v-col cols="7" class="text-body-1 font-weight-bold">Saldo:</v-col>
                <v-col cols="5" class="text-body-1 font-weight-bold text-right" :class="resultadoMovimientos.saldo >= 0 ? 'green--text' : 'red--text'">{{ resultadoMovimientos.saldoString }}</v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- Tabla de movimientos -->
        <v-data-table
          v-if="movimientos.length"
          :headers="headersMovimientos"
          :items="movimientos"
          class="elevation-1 mt-3"
          locale="es-ar"
          item-key="idCobro"
          :items-per-page="50"
          :item-class="rowClasses"
          :disable-sort="true"
          :footer-props="{ itemsPerPageText: 'Filas por página' }"
        >
        </v-data-table>
        <v-alert v-else-if="buscado" class="mt-4" color="purple" dark outlined>
          No hay movimientos para el período seleccionado.
        </v-alert>

        <!-- Paginación backend -->
        <v-row v-if="resultadoMovimientos && resultadoMovimientos.totalRegistros > registrosPorPagina" class="mt-2" justify="center">
          <v-pagination
            v-model="pagina"
            :length="Math.ceil(resultadoMovimientos.totalRegistros / registrosPorPagina)"
            @input="buscarMovimientos"
          ></v-pagination>
        </v-row>
      </v-card-text>

      <!-- Acciones -->
      <v-card-actions>
        <v-btn dark color="#24232d" @click="salir">
          <v-icon left>mdi-arrow-left</v-icon> Volver
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="teal" dark @click="showApertura = true" :disabled="!cajaSeleccionada">
          <v-icon left>mdi-cash-register</v-icon> Apertura
        </v-btn>
        <v-btn color="red" dark @click="showRetiro = true" :disabled="!cajaSeleccionada">
          <v-icon left>mdi-cash-minus</v-icon> Retiro
        </v-btn>
        <v-btn color="orange" dark @click="showTransferencia = true" :disabled="saldosCajas.length < 2">
          <v-icon left>mdi-swap-horizontal</v-icon> Transferencia
        </v-btn>
        <v-btn color="blue-grey" dark @click="$router.push('/caja/configuracion_cajas')">
          <v-icon left>mdi-cog</v-icon> Configurar
        </v-btn>
      </v-card-actions>

      <!-- Dialog Apertura -->
      <v-dialog v-model="showApertura" max-width="400" persistent>
        <v-card class="mb-0">
          <v-toolbar dark color="teal" dense>
            <v-toolbar-title>Apertura de Caja</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="showApertura = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-4">
            <v-text-field
              v-model="montoApertura"
              label="Monto Inicial *"
              prepend-icon="mdi-currency-usd"
              type="number"
              dense
            ></v-text-field>
            <v-text-field
              v-model="obsApertura"
              label="Observaciones"
              prepend-icon="mdi-note"
              dense
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showApertura = false">Cancelar</v-btn>
            <v-btn color="teal" dark @click="registrarApertura" :disabled="!montoApertura || montoApertura <= 0">Registrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog Retiro -->
      <v-dialog v-model="showRetiro" max-width="400" persistent>
        <v-card class="mb-0">
          <v-toolbar dark color="red" dense>
            <v-toolbar-title>Retiro de Caja</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="showRetiro = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-4">
            <v-text-field
              v-model="montoRetiro"
              label="Monto a retirar *"
              prepend-icon="mdi-currency-usd"
              type="number"
              dense
            ></v-text-field>
            <v-text-field
              v-model="obsRetiro"
              label="Observaciones (ej: Retiro dueño)"
              prepend-icon="mdi-note"
              dense
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showRetiro = false">Cancelar</v-btn>
            <v-btn color="red" dark @click="registrarRetiro" :disabled="!montoRetiro || montoRetiro <= 0">Registrar Retiro</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog Transferencia -->
      <v-dialog v-model="showTransferencia" max-width="450" persistent>
        <v-card>
          <v-toolbar dark color="orange" dense>
            <v-toolbar-title>Transferencia entre Cajas</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="showTransferencia = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-4">
            <v-select
              :items="saldosCajas"
              item-text="nombreCaja"
              item-value="idCaja"
              label="Caja Origen *"
              v-model="transfOrigen"
              dense
              prepend-icon="mdi-bank-minus"
            ></v-select>
            <v-select
              :items="saldosCajas.filter(c => c.idCaja !== transfOrigen)"
              item-text="nombreCaja"
              item-value="idCaja"
              label="Caja Destino *"
              v-model="transfDestino"
              dense
              prepend-icon="mdi-bank-plus"
            ></v-select>
            <v-text-field
              v-model="transfMonto"
              label="Monto *"
              prepend-icon="mdi-currency-usd"
              type="number"
              dense
            ></v-text-field>
            <v-text-field
              v-model="transfObs"
              label="Observaciones"
              prepend-icon="mdi-note"
              dense
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showTransferencia = false">Cancelar</v-btn>
            <v-btn color="orange" dark @click="registrarTransferencia" :disabled="!transfOrigen || !transfDestino || !transfMonto || transfMonto <= 0">Transferir</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</template>

<style scoped>
.container { margin: auto; }
.border-primary { border-color: #1976d2 !important; border-width: 2px !important; }
.background-red { background-color: rgb(255, 82, 82); color: white; }
.background-red:hover { background-color: rgb(228, 64, 64) !important; color: white !important; }
</style>

<script>
import axios from "@/axios-auth";
import router from "@/router/index";
import { showAlert } from "@/utils/toast";
import moment from "moment";

export default {
  data: () => ({
    saldosCajas: [],
    cajaSeleccionada: null,
    nombreCajaSeleccionada: "",
    saldoDesdeCierre: null,
    periodos: [
      { texto: "Desde último cierre", valor: "cierre" },
      { texto: "Hoy", valor: "hoy" },
      { texto: "Última semana", valor: "semana" },
      { texto: "Último mes", valor: "mes" },
      { texto: "Todo el historial", valor: "todo" },
      { texto: "Rango personalizado", valor: "personalizado" },
    ],
    periodoSeleccionado: "cierre",
    fechaDesde: null,
    fechaHasta: null,
    movimientos: [],
    resultadoMovimientos: null,
    buscado: false,
    pagina: 1,
    registrosPorPagina: 100,
    headersMovimientos: [
      { text: "Fecha", value: "fechaString" },
      { text: "Concepto", value: "paciente" },
      { text: "Tipo", value: "tipoMovimiento" },
      { text: "Forma Pago", value: "nombreFormaPago" },
      { text: "Ingreso", value: "ingresoString" },
      { text: "Egreso", value: "egresoString" },
      { text: "Saldo Acum.", value: "saldoAcumuladoString" },
      { text: "Usuario", value: "usuario" },
    ],
    // Apertura
    showApertura: false,
    montoApertura: null,
    obsApertura: "",
    // Retiro
    showRetiro: false,
    montoRetiro: null,
    obsRetiro: "",
    // Transferencia
    showTransferencia: false,
    transfOrigen: null,
    transfDestino: null,
    transfMonto: null,
    transfObs: "",
  }),
  methods: {
    rowClasses(item) {
      if (item.total < 0) return "background-red";
      return "";
    },
    salir() {
      router.push({ path: "/home" });
    },
    async cargarSaldos() {
      try {
        const { data } = await axios.get("/Caja/BuscarSaldoCajas");
        this.saldosCajas = data;
        if (this.saldosCajas.length && !this.cajaSeleccionada) {
          this.seleccionarCaja(this.saldosCajas[0]);
        }
      } catch (e) { /* silenciar */ }
    },
    async seleccionarCaja(caja) {
      this.cajaSeleccionada = caja.idCaja;
      this.nombreCajaSeleccionada = caja.nombreCaja;
      this.pagina = 1;
      this.buscado = false;
      this.movimientos = [];
      this.resultadoMovimientos = null;

      // Cargar saldo desde cierre
      try {
        const { data } = await axios.get("/Caja/BuscarSaldoDesdeCierre", {
          params: { idCaja: caja.idCaja },
        });
        this.saldoDesdeCierre = data;
      } catch (e) { this.saldoDesdeCierre = null; }

      // Auto-buscar con período default
      this.buscarMovimientos();
    },
    cambioPeriodo() {
      this.pagina = 1;
      if (this.periodoSeleccionado !== "personalizado") {
        this.buscarMovimientos();
      }
    },
    getFechas() {
      const hoy = moment().format("YYYY-MM-DD");
      switch (this.periodoSeleccionado) {
        case "cierre":
          return {
            fechaDesde: this.saldoDesdeCierre?.fechaUltimoCierre
              ? moment(this.saldoDesdeCierre.fechaUltimoCierre).format("YYYY-MM-DD")
              : null,
            fechaHasta: hoy,
          };
        case "hoy":
          return { fechaDesde: hoy, fechaHasta: hoy };
        case "semana":
          return { fechaDesde: moment().subtract(7, "days").format("YYYY-MM-DD"), fechaHasta: hoy };
        case "mes":
          return { fechaDesde: moment().subtract(1, "month").format("YYYY-MM-DD"), fechaHasta: hoy };
        case "todo":
          return { fechaDesde: null, fechaHasta: null };
        case "personalizado":
          return { fechaDesde: this.fechaDesde, fechaHasta: this.fechaHasta };
        default:
          return { fechaDesde: hoy, fechaHasta: hoy };
      }
    },
    async buscarMovimientos() {
      if (!this.cajaSeleccionada) return;
      const { fechaDesde, fechaHasta } = this.getFechas();
      try {
        const { data } = await axios.get("/Caja/BuscarMovimientosCaja", {
          params: {
            idCaja: this.cajaSeleccionada,
            fechaDesde,
            fechaHasta,
            pagina: this.pagina,
            registrosPorPagina: this.registrosPorPagina,
          },
        });
        this.resultadoMovimientos = data;
        // Agregar columnas de ingreso/egreso para la tabla
        this.movimientos = (data.movimientos || []).map((m) => ({
          ...m,
          ingresoString: m.total >= 0 ? m.totalString : "",
          egresoString: m.total < 0 ? m.totalString : "",
          tipoMovimiento: m.tipoMovimiento || "Normal",
        }));
        this.buscado = true;
      } catch (e) {
        this.movimientos = [];
        this.resultadoMovimientos = null;
        this.buscado = true;
      }
    },
    async registrarApertura() {
      try {
        await axios.post("/Caja/AperturaCaja", {
          idCaja: this.cajaSeleccionada,
          monto: parseFloat(this.montoApertura),
          observaciones: this.obsApertura,
          idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
          usuario: this.$store.state.usuario.usuario,
        });
        showAlert({ icon: "success", title: "Apertura registrada.", vm: this, timer: 2000 });
        this.showApertura = false;
        this.montoApertura = null;
        this.obsApertura = "";
        this.cargarSaldos();
        this.buscarMovimientos();
      } catch (e) {
        showAlert({ icon: "warning", title: "Ya existe una apertura para hoy o hubo un error.", vm: this, timer: 3000 });
      }
    },
    async registrarRetiro() {
      try {
        const hoy = new Date();
        const fechaLocal = hoy.getFullYear() + '-' + 
          String(hoy.getMonth() + 1).padStart(2, '0') + '-' + 
          String(hoy.getDate()).padStart(2, '0');
        await axios.post("/Caja/GuardarCobro", {
          fecha: fechaLocal,
          total: -Math.abs(parseFloat(this.montoRetiro)),
          paciente: "Retiro de Caja",
          observaciones: this.obsRetiro || "Retiro de efectivo",
          idProfesional: 0,
          idFormaPago: 1,
          usuario: this.$store.state.usuario.usuario,
          idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
          idCaja: this.cajaSeleccionada,
          tipoMovimiento: "RETIRO",
          esSena: false,
        });
        showAlert({ icon: "success", title: "Retiro registrado.", vm: this, timer: 2000 });
        this.showRetiro = false;
        this.montoRetiro = null;
        this.obsRetiro = "";
        this.cargarSaldos();
        this.buscarMovimientos();
      } catch (e) {
        showAlert({ icon: "warning", title: "Error al registrar el retiro.", vm: this, timer: 3000 });
      }
    },
    async registrarTransferencia() {
      try {
        await axios.post("/Caja/TransferenciaCaja", {
          idCajaOrigen: this.transfOrigen,
          idCajaDestino: this.transfDestino,
          monto: parseFloat(this.transfMonto),
          observaciones: this.transfObs,
          idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
          usuario: this.$store.state.usuario.usuario,
        });
        showAlert({ icon: "success", title: "Transferencia registrada.", vm: this, timer: 2000 });
        this.showTransferencia = false;
        this.transfOrigen = null;
        this.transfDestino = null;
        this.transfMonto = null;
        this.transfObs = "";
        this.cargarSaldos();
        this.buscarMovimientos();
      } catch (e) {
        showAlert({ icon: "warning", title: "Error al registrar la transferencia.", vm: this, timer: 3000 });
      }
    },
  },
  async created() {
    await this.cargarSaldos();
  },
};
</script>